import { FormControl, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HelpPopover from '../../../HelpPopover';
import { updateParameter } from '../DeviceCardBuilder';
import { DeviceCardProps } from '../DeviceCardProps';
import { useControlInitialization } from '../../../../hooks/controls/useControlInitialization';
import { formatNumericRangeBound, getSupportedNumericRangeText } from '../../../../utils/numericRangeText';
import { isPlainNumericLiteral, isUnsupportedNumericRawValue } from './unsupportedNumericValue';

interface InputControlProps extends DeviceCardProps {
  label: React.ReactNode;
  labelId: string;
  labelPopover?: React.ReactNode;
  labelPopoverId?: string;
  step: number;
  min?: number;
  max?: number;
  maxDecimalPlaces?: number;
  unitLabel?: React.ReactNode;
  /** When true, show a blank field until the parameter exists in code or the user edits it. */
  showEmptyWhenUnset?: boolean;
}

const isValidMaxDecimalPlaces = (value: number | undefined): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 100;

const formatNormalizedNumber = (value: number, maxDecimalPlaces?: number): string => {
  if (!isValidMaxDecimalPlaces(maxDecimalPlaces)) return value.toString();
  return Number(value.toFixed(maxDecimalPlaces)).toString();
};

const normalizeNumber = (value: number, maxDecimalPlaces?: number): number => {
  if (!isValidMaxDecimalPlaces(maxDecimalPlaces)) return value;
  return Number(formatNormalizedNumber(value, maxDecimalPlaces));
};

const formatNumber = (value: number | undefined, fallback: number, maxDecimalPlaces?: number, emptyWhenUnset = false): string => {
  if (emptyWhenUnset && (typeof value !== 'number' || !Number.isFinite(value))) return '';
  if (typeof value === 'number' && Number.isFinite(value)) return formatNormalizedNumber(value, maxDecimalPlaces);
  return fallback.toString();
};

const formatWrittenNumber = (value: number | undefined, fallback: number, emptyWhenUnset = false): string => {
  if (emptyWhenUnset && (typeof value !== 'number' || !Number.isFinite(value))) return '';
  if (typeof value === 'number' && Number.isFinite(value)) return value.toString();
  return fallback.toString();
};

const getUnitText = (unitLabel: React.ReactNode): string => {
  if (typeof unitLabel === 'string' || typeof unitLabel === 'number') return String(unitLabel).trim();
  return '';
};

const parseNumericDraft = (value: string): number | null => {
  const trimmed = value.trim();
  if (!isPlainNumericLiteral(trimmed)) return null;
  const parsed = Number.parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const InputControl = (props: InputControlProps) => {
  const { t } = useTranslation();
  const {
    value: inputValue,
    setValue: setInputValue,
    unsupportedRawValue,
    isUnset,
  } = useControlInitialization<number>(props, {
    min: props.min,
    max: props.max,
    parseValue: (v) => Number.parseFloat(v),
    isUnsupportedRawValue: isUnsupportedNumericRawValue,
  });
  const showEmptyWhenUnset = props.showEmptyWhenUnset === true;
  const useEmptyDisplay = showEmptyWhenUnset && isUnset;
  const fallbackValue = props.min ?? 0;
  const committedValue = formatNumber(inputValue, fallbackValue, props.maxDecimalPlaces, useEmptyDisplay);
  const writtenValue = formatWrittenNumber(inputValue, fallbackValue, useEmptyDisplay);
  const [draftValue, setDraftValue] = useState(committedValue);
  const [isEditing, setIsEditing] = useState(false);
  const [rangeError, setRangeError] = useState<string | null>(null);
  const lastWrittenValueRef = useRef(writtenValue);
  const lastDisplayValueRef = useRef(committedValue);
  const unsupportedReplacementRawValueRef = useRef<string | undefined>(unsupportedRawValue);
  const unsupportedReplacementCommittedRef = useRef(false);
  const suppressNextBlurCommitRef = useRef(false);

  const isUnsupported = Boolean(unsupportedRawValue);
  const unsupportedDisplayValue = t('controlPanel.devices.unsupportedCode.title', { defaultValue: 'Custom code' });
  const unsupportedHelperText = unsupportedRawValue
    ? t('controlPanel.devices.unsupportedNumericParameter.description', {
        defaultValue: 'Unsupported parameter value: {{code}}',
        code: unsupportedRawValue,
      })
    : undefined;

  useEffect(() => {
    if (unsupportedReplacementRawValueRef.current === unsupportedRawValue) return;
    unsupportedReplacementRawValueRef.current = unsupportedRawValue;
    unsupportedReplacementCommittedRef.current = false;
  }, [unsupportedRawValue]);

  useEffect(() => {
    if (isUnsupported && !isEditing) {
      setDraftValue('');
      return;
    }
    if (!isEditing && !rangeError) {
      setDraftValue(committedValue);
    }
    lastWrittenValueRef.current = writtenValue;
    lastDisplayValueRef.current = committedValue;
  }, [committedValue, isEditing, isUnsupported, rangeError, writtenValue]);

  const buildRangeError = () => {
    if (typeof props.min === 'number' && typeof props.max === 'number') {
      const min = formatNumericRangeBound(props.min);
      const max = formatNumericRangeBound(props.max);
      return t('inputControl.numeric_range_error', {
        defaultValue: 'Enter a value from {{min}} to {{max}}',
        min,
        max,
      });
    }
    if (typeof props.min === 'number') {
      const min = formatNumericRangeBound(props.min);
      return t('inputControl.numeric_min_error', {
        defaultValue: 'Enter a value of at least {{min}}',
        min,
      });
    }
    if (typeof props.max === 'number') {
      const max = formatNumericRangeBound(props.max);
      return t('inputControl.numeric_max_error', {
        defaultValue: 'Enter a value no more than {{max}}',
        max,
      });
    }
    return t('inputControl.numeric_invalid_error', {
      defaultValue: 'Enter a valid number.',
    });
  };

  const buildSupportedRangeText = () => {
    return getSupportedNumericRangeText(t, { min: props.min, max: props.max, unit: getUnitText(props.unitLabel) });
  };

  const isInRange = (value: number) => {
    if (typeof props.min === 'number' && value < props.min) return false;
    if (typeof props.max === 'number' && value > props.max) return false;
    return true;
  };

  const commitParsedValue = (parsed: number, options: { normalizeDraft: boolean; finishEditing: boolean }): boolean => {
    const normalized = normalizeNumber(parsed, props.maxDecimalPlaces);
    const nextValue = formatNormalizedNumber(normalized, props.maxDecimalPlaces);
    if (options.normalizeDraft) {
      setDraftValue(nextValue);
    }
    setInputValue(normalized);
    setRangeError(null);
    if (options.finishEditing) {
      setIsEditing(false);
    }
    const shouldReplaceUnsupported = isUnsupported && !unsupportedReplacementCommittedRef.current;
    if (shouldReplaceUnsupported || lastWrittenValueRef.current !== nextValue) {
      lastWrittenValueRef.current = nextValue;
      lastDisplayValueRef.current = nextValue;
      if (shouldReplaceUnsupported) unsupportedReplacementCommittedRef.current = true;
      updateParameter(props, nextValue, { focusEditor: false });
    }
    return true;
  };

  const validateDraftForEditing = (value: string) => {
    const parsed = parseNumericDraft(value);
    if (parsed === null) {
      setRangeError(null);
      return;
    }

    if (!isInRange(parsed)) {
      setRangeError(buildRangeError());
      return;
    }

    commitParsedValue(parsed, { normalizeDraft: false, finishEditing: false });
  };

  const revertDraft = () => {
    setDraftValue(isUnsupported ? '' : lastDisplayValueRef.current);
    setRangeError(null);
    setIsEditing(false);
  };

  const commitDraft = (options: { revertOnInvalid: boolean }): boolean => {
    const parsed = parseNumericDraft(draftValue);
    if (parsed === null) {
      if (options.revertOnInvalid) {
        revertDraft();
      }
      return false;
    }

    if (!isInRange(parsed)) {
      if (options.revertOnInvalid) {
        revertDraft();
      } else {
        setRangeError(buildRangeError());
      }
      return false;
    }

    return commitParsedValue(parsed, { normalizeDraft: true, finishEditing: true });
  };

  const onChange = (value: string) => {
    setDraftValue(value);
    validateDraftForEditing(value);
  };

  const onFocus = () => {
    setIsEditing(true);
    if (isUnsupported) {
      setDraftValue('');
      setRangeError(null);
    }
  };

  const onBlur = () => {
    if (suppressNextBlurCommitRef.current) {
      suppressNextBlurCommitRef.current = false;
      return;
    }
    if (isUnsupported && draftValue.trim() === '') {
      revertDraft();
      return;
    }
    commitDraft({ revertOnInvalid: true });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const committed = commitDraft({ revertOnInvalid: false });
      if (committed) {
        suppressNextBlurCommitRef.current = true;
        event.currentTarget.blur();
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      revertDraft();
      suppressNextBlurCommitRef.current = true;
      event.currentTarget.blur();
    }
  };

  const rangeHelperText = buildSupportedRangeText();
  const displayValue = isUnsupported && !isEditing ? unsupportedDisplayValue : draftValue;
  const shrinkInputLabel = !isUnsupported && (useEmptyDisplay || displayValue === '');

  return (
    <FormControl variant="outlined">
      <TextField
        slotProps={{
          htmlInput: { step: props.step, min: props.min, max: props.max, inputMode: 'decimal' },
          input: props.unitLabel
            ? {
                endAdornment: <InputAdornment position="end">{props.unitLabel}</InputAdornment>,
              }
            : undefined,
          inputLabel: shrinkInputLabel ? { shrink: true } : undefined,
        }}
        id={props.labelId}
        label={
          props.labelPopover && props.labelPopoverId ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography>{props.label}</Typography>
              <HelpPopover id={props.labelPopoverId} text={props.labelPopover} />
            </Stack>
          ) : (
            props.label
          )
        }
        type={isUnsupported && !isEditing ? 'text' : 'number'}
        value={displayValue}
        error={!!rangeError}
        helperText={rangeError ?? unsupportedHelperText ?? rangeHelperText ?? undefined}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onChange={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange(event.target.value)}
      />
    </FormControl>
  );
};

export default InputControl;
